import type { ConsentTheme, EntryPosition } from './types';

const MODAL_WIDTHS: Record<ConsentTheme['modal_size'], string> = {
  sm: '440px',
  md: '640px',
  lg: '820px',
};

const SYSTEM_FONT =
  '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

type Rgb = { r: number; g: number; b: number };

function hexToRgb(hex: string): Rgb {
  const value = hex.replace('#', '');
  const full = value.length === 3
    ? value
        .split('')
        .map((c) => c + c)
        .join('')
    : value;

  return {
    r: parseInt(full.slice(0, 2), 16) || 0,
    g: parseInt(full.slice(2, 4), 16) || 0,
    b: parseInt(full.slice(4, 6), 16) || 0,
  };
}

function rgbToCss({ r, g, b }: Rgb, alpha?: number): string {
  return alpha === undefined ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function mix(a: Rgb, b: Rgb, weight: number): Rgb {
  return {
    r: Math.round(a.r + (b.r - a.r) * weight),
    g: Math.round(a.g + (b.g - a.g) * weight),
    b: Math.round(a.b + (b.b - a.b) * weight),
  };
}

/** Relative luminance, used to pick readable text on the primary color. */
function luminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function entryOffsets(position: EntryPosition): Record<string, string> {
  const gap = '20px';
  const [vertical, horizontal] = position.split('_');

  return {
    '--orbit-entry-top': vertical === 'top' ? gap : 'auto',
    '--orbit-entry-bottom': vertical === 'bottom' ? gap : 'auto',
    '--orbit-entry-left': horizontal === 'left' ? gap : 'auto',
    '--orbit-entry-right': horizontal === 'right' ? gap : 'auto',
  };
}

/** Publishes the theme as CSS custom properties the consent UI reads. */
export function applyTheme(theme: ConsentTheme): void {
  const surface = hexToRgb(theme.surface);
  const text = hexToRgb(theme.text);
  const primary = hexToRgb(theme.primary);

  const vars: Record<string, string> = {
    '--orbit-primary': theme.primary,
    '--orbit-primary-hover': rgbToCss(mix(primary, { r: 0, g: 0, b: 0 }, 0.12)),
    '--orbit-on-primary': luminance(primary) > 0.5 ? '#0A0E17' : '#FFFFFF',
    '--orbit-surface': theme.surface,
    '--orbit-surface-alt': rgbToCss(mix(surface, text, 0.08)),
    '--orbit-border': rgbToCss(mix(surface, text, 0.16)),
    '--orbit-text': theme.text,
    '--orbit-muted': theme.muted,
    '--orbit-overlay': rgbToCss(mix(surface, { r: 0, g: 0, b: 0 }, 0.35), 0.72),
    '--orbit-shadow': `0 24px 80px ${rgbToCss({ r: 0, g: 0, b: 0 }, 0.45)}`,
    '--orbit-radius': `${theme.radius}px`,
    '--orbit-radius-lg': `${theme.radius + 6}px`,
    '--orbit-font': theme.font === 'inherit' ? 'inherit' : SYSTEM_FONT,
    '--orbit-modal-width': MODAL_WIDTHS[theme.modal_size] || MODAL_WIDTHS.md,
    ...entryOffsets(theme.entry_position),
  };

  const root = document.documentElement;
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
}
